import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { API_URL } from "../config/api";
import { NEWS_PAGE_SIZE, formatNewsDate } from "../config/constants";

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --black: #0a0a0a;
    --dark: #111111;
    --card: #161616;
    --border: #2a2a2a;
    --gold:  #0093c0;
    --gold-light:  #0093c0;
    --white: #f5f0e8;
    --muted: #888;
  }

@font-face {
  font-family: 'Sippy';
  /* Adjust the path and filenames to match your font files */
  src: url('../font/GothamBook.woff2') format('woff2');
  font-weight: 400; /* Regular */
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'SippyBold';
  /* Adjust the path and filenames to match your font files */
  src: url('../font/Gotham-Bold.woff2') format('woff2');
  font-weight: 800; /* Bold or Black, adjust as needed */
  font-style: normal;
  font-display: swap;
}
  .sf-page {
    background: var(--black);
    color: var(--white);
    font-family: 'Sippy', sans-serif;
    font-weight: 300;
    line-height: 1.6;
    min-height: 100vh;
    overflow-x: hidden;
  }

  /* HERO */
  .sf-hero {
    text-align: center;
    padding: 80px 60px 50px;
    position: relative;
    overflow: hidden;
  }
  .sf-hero-bg {
    position: absolute; inset: 0;
    font-family: 'SippyBold', sans-serif;
    font-size: clamp(100px, 20vw, 260px);
    font-weight: 900;
    color: rgba(255,255,255,0.025);
    display: flex; align-items: center; justify-content: center;
    pointer-events: none; user-select: none;
    
    letter-spacing: -0.05em;
  }
  .sf-hero h1 {
    font-family: 'SippyBold', sans-serif;
    font-size: clamp(3.0rem, 10vw, 3.8rem);
    font-weight: 900;
    color:#fffff;
      margin-top: 30px;
    letter-spacing: -0.02em;
    line-height: 1;
    position: relative;
    animation: fadeUp 0.8s ease both;
  }
  .sf-hero h1 .dot { color: var(--gold); }
  .sf-hero-sub {
    color: var(--muted);
    font-size: 0.8rem;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    margin-top: 14px;
    position: relative;
    animation: fadeUp 0.8s 0.15s ease both;
  }
  .sf-divider {
    width: 50px; height: 2px;
    background: var(--gold);
    margin: 20px auto 0;
    position: relative;
    animation: scaleX 0.6s 0.3s ease both;
    transform-origin: left;
  }

  /* FILTER */
  .sf-filter {
    display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;
    padding: 36px 60px 16px;
    animation: fadeUp 0.6s 0.2s ease both;
  }
  .sf-filter-btn {
    padding: 7px 20px;
    border: 1px solid var(--border);
    background: none;
    color: var(--muted);
    font-family: 'Sippy', sans-serif;
    font-size: 0.72rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    cursor: pointer;
    transition: all 0.2s;
    border-radius: 2px;
  }
  .sf-filter-btn:hover { border-color: var(--gold); color: var(--gold); background: rgba(201,168,76,0.05); }
  .sf-filter-btn.active { border-color: var(--gold); color: var(--gold); background: rgba(201,168,76,0.08); }

  /* NEWSLETTER BANNER */
  .sf-newsletter {
    margin: 24px 60px;
    background: linear-gradient(135deg, #131108 0%, #1a1508 100%);
    border: 1px solid rgba(201,168,76,0.4);
    padding: 36px 52px;
    display: flex; align-items: center; justify-content: space-between; gap: 40px;
    position: relative; overflow: hidden;
    animation: fadeUp 0.7s 0.3s ease both;
  }
  .sf-newsletter::before {
    content: '✦';
    position: absolute; right: 40px; top: 50%; transform: translateY(-50%);
    font-size: 140px; color: rgba(201,168,76,0.03); pointer-events: none;
  }
  .sf-newsletter h3 {
    font-family: 'SippyBold', sans-serif;
    font-size: 1.5rem; font-weight: 700; line-height: 1.2;
  }
  .sf-newsletter p { color: var(--muted); font-size: 0.88rem; margin-top: 5px; }
  .sf-nl-form { display: flex; flex-shrink: 0; }
  .sf-nl-input {
    background: rgba(255,255,255,0.05);
    border: 1px solid var(--border);
    border-right: none;
    padding: 13px 22px;
    color: var(--white);
    font-family: 'Sippy', sans-serif;
    font-size: 0.88rem;
    width: 260px; outline: none;
    transition: border-color 0.2s;
  }
  .sf-nl-input:focus { border-color: var(--gold); }
  .sf-nl-input::placeholder { color: var(--muted); }
  .sf-nl-btn {
    background: var(--gold); color: var(--black);
    border: none; padding: 13px 26px;
    font-family: 'Sippy', sans-serif;
    font-size: 0.72rem; font-weight: 500;
    letter-spacing: 0.1em; text-transform: uppercase;
    cursor: pointer; transition: background 0.2s;
    white-space: nowrap;
  }
  .sf-nl-btn:hover { background: var(--gold-light); }

  /* FEATURED */
  .sf-featured {
    margin: 40px 60px;
    display: grid; grid-template-columns: 1.1fr 1fr;
    border: 1px solid var(--border);
    overflow: hidden;
    transition: border-color 0.35s;
    cursor: pointer;
    animation: fadeUp 0.7s 0.1s ease both;
  }
  .sf-featured:hover { border-color: var(--gold); }
  .sf-feat-img { position: relative; overflow: hidden; min-height: 460px; }
  .sf-feat-img img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.7s ease; display: block; }
  .sf-featured:hover .sf-feat-img img { transform: scale(1.04); }
  .sf-feat-tag {
    position: absolute; top: 22px; left: 22px;
    background: var(--gold); color: var(--black);
    font-size: 0.65rem; letter-spacing: 0.12em; text-transform: uppercase;
    padding: 5px 12px; font-weight: 500;
  }
  .sf-feat-body {
    background: var(--card);
    padding: 56px 48px;
    display: flex; flex-direction: column; justify-content: center;
  }
  .sf-feat-meta {
    font-size: 0.7rem; letter-spacing: 0.15em; text-transform: uppercase;
    color: var(--gold); margin-bottom: 14px;
  }
  .sf-feat-body h2 {
    font-family: 'SippyBold', sans-serif;
    font-size: clamp(1.5rem, 2.8vw, 2.2rem);
    font-weight: 700; line-height: 1.2; margin-bottom: 18px;
  }
  .sf-feat-body p {
    color: var(--muted); font-size: 0.93rem; line-height: 1.85;
    margin-bottom: 30px;
  }
  .sf-read-btn {
    display: inline-block; padding: 12px 28px;
    border: 1px solid var(--gold); color: var(--gold);
    font-size: 0.72rem; letter-spacing: 0.12em; text-transform: uppercase;
    text-decoration: none; cursor: pointer; background: none;
    font-family: 'Sippy', sans-serif;
    transition: all 0.25s; align-self: flex-start;
  }
  .sf-read-btn:hover { background: var(--gold); color: var(--black); }

  /* SECTION HEADER */
  .sf-section-hd {
    padding: 52px 60px 28px;
    display: flex; align-items: center; gap: 20px;
  }
  .sf-section-hd h2 {
    font-family: 'Sippy', sans-serif;
    font-size: 0.72rem; font-weight: 400;
    letter-spacing: 0.2em; text-transform: uppercase;
    color: var(--muted); white-space: nowrap;
  }
  .sf-section-hd-line {
    flex: 1; height: 1px; background: var(--border);
  }

  /* NEWS GRID */
  .sf-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1px;
  
    margin: 0 60px;
  }
  .sf-card {
    background: var(--black);
    overflow: hidden; cursor: pointer;
    transition: background 0.3s;
  }
  .sf-card:hover { background: var(--card); }
  .sf-card-img { position: relative; overflow: hidden; aspect-ratio: 16/10; }
  .sf-card-img img {
    width: 100%; height: 100%; object-fit: cover; display: block;
    transition: transform 0.5s ease; filter: brightness(0.82);
  }
  .sf-card:hover .sf-card-img img { transform: scale(1.06); filter: brightness(1); }
  .sf-tag {
    position: absolute; bottom: 14px; left: 14px;
    font-size: 0.62rem; letter-spacing: 0.1em; text-transform: uppercase;
    padding: 4px 10px; font-weight: 500;
  }
  .tag-events { background: #2563eb; color: #fff; }
  .tag-theatrical { background: #7c3aed; color: #fff; }
  .tag-production { background: #db2777; color: #fff; }
  .tag-legacy { background: #059669; color: #fff; }
  .tag-corporate { background: var(--gold); color: var(--black); }
  .tag-interview { background: #dc2626; color: #fff; }
  .tag-awards { background: #d97706; color: #fff; }
  .tag-streaming { background: #0891b2; color: #fff; }

  .sf-card-body { padding: 22px; }
  .sf-card-date { font-size: 0.67rem; color: var(--muted); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 9px; }
  .sf-card-body h3 {
    font-family: 'SippyBold', sans-serif;
    font-size: 1.05rem; font-weight: 700; line-height: 1.35;
    margin-bottom: 9px; transition: color 0.2s;
  }
  .sf-card:hover .sf-card-body h3 { color: var(--gold); }
  .sf-card-body p {
    color: var(--muted); font-size: 0.82rem; line-height: 1.7;
    display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
    margin-bottom: 14px;
  }
  .sf-readmore {
    font-size: 0.7rem; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--gold); display: inline-flex; align-items: center; gap: 6px;
    transition: gap 0.2s; text-decoration: none; cursor: pointer; background: none; border: none; padding: 0; font-family: 'Sippy', sans-serif;
  }
  .sf-readmore:hover { gap: 10px; }

  /* SIDEBAR LAYOUT */
  .sf-with-sidebar {
    display: grid; grid-template-columns: 1fr 320px;
    gap: 40px; margin: 60px 60px 0;
    align-items: start;
  }

  /* TIMELINE */
  .sf-timeline-title {
    font-family: 'SippyBold', sans-serif;
    font-size: 1.15rem; font-weight: 700;
    margin-bottom: 28px; padding-bottom: 14px;
    border-bottom: 1px solid var(--border);
  }
  .sf-timeline { position: relative; }
  .sf-timeline::before {
    content: ''; position: absolute; left: 110px; top: 0; bottom: 0;
    width: 1px; background: var(--border);
  }
  .sf-tl-item {
    display: flex; gap: 36px; margin-bottom: 28px;
    padding-bottom: 28px; border-bottom: 1px solid rgba(42,42,42,0.5);
    position: relative;
  }
  .sf-tl-item:last-child { border-bottom: none; }
  .sf-tl-date {
    width: 110px; flex-shrink: 0; text-align: right;
    font-size: 0.68rem; color: var(--muted); letter-spacing: 0.05em; padding-top: 4px;
  }
  .sf-tl-dot {
    position: absolute; left: 103px; top: 7px;
    width: 14px; height: 14px;
    border: 2px solid var(--gold); background: var(--black); border-radius: 50%;
  }
  .sf-tl-content { padding-left: 22px; flex: 1; }
  .sf-tl-tag {
    font-size: 0.62rem; letter-spacing: 0.1em; text-transform: uppercase;
    padding: 3px 8px; display: inline-block; margin-bottom: 7px; border-radius: 2px;
  }
  .sf-tl-content h4 {
    font-family: 'SippyBold', sans-serif;
    font-size: 1rem; font-weight: 700; margin-bottom: 5px;
  }
  .sf-tl-content p { color: var(--muted); font-size: 0.83rem; line-height: 1.7; }

  /* SIDEBAR */
  .sf-trending {
    background: var(--card); border: 1px solid var(--border); padding: 28px;
  }
  .sf-trending h3 {
    font-family: 'SippyBold', sans-serif;
    font-size: 0.88rem; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--gold); margin-bottom: 20px;
    padding-bottom: 14px; border-bottom: 1px solid var(--border);
  }
  .sf-trend-item {
    display: flex; gap: 14px; align-items: flex-start;
    padding: 14px 0; border-bottom: 1px solid var(--border);
    cursor: pointer;
  }
  .sf-trend-item:last-child { border-bottom: none; padding-bottom: 0; }
  .sf-trend-num {
    font-family: 'SippyBold', sans-serif;
    font-size: 1.8rem; font-weight: 900; color: rgba(201,168,76,0.12);
    line-height: 1; flex-shrink: 0; width: 32px;
    transition: color 0.2s;
  }
  .sf-trend-item:hover .sf-trend-num { color: var(--gold); }
  .sf-trend-text .date { font-size: 0.62rem; color: var(--muted); letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 3px; }
  .sf-trend-text h4 { font-family: 'SippyBold', sans-serif; font-size: 0.88rem; line-height: 1.3; }
  .sf-trend-item:hover .sf-trend-text h4 { color: var(--gold); }

  .sf-bites { margin-top: 28px; }
  .sf-bites h3 {
    font-family: 'SippyBold', sans-serif;
    font-size: 0.88rem; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--gold); margin-bottom: 18px;
  }
  .sf-bite { display: flex; gap: 14px; margin-bottom: 18px; padding-bottom: 18px; border-bottom: 1px solid var(--border); }
  .sf-bite:last-child { border-bottom: none; margin-bottom: 0; }
  .sf-bite img { width: 76px; height: 76px; object-fit: cover; flex-shrink: 0; }
  .sf-bite-text .date { font-size: 0.62rem; color: var(--muted); letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 4px; }
  .sf-bite-text p { font-size: 0.82rem; line-height: 1.5; color: var(--white); }

  /* LOAD MORE */
  .sf-load-more { text-align: center; padding: 60px; }
  .sf-load-btn {
    padding: 14px 46px; border: 1px solid var(--border);
    background: none; color: var(--white);
    font-family: 'Sippy', sans-serif;
    font-size: 0.75rem; letter-spacing: 0.12em; text-transform: uppercase;
    cursor: pointer; transition: all 0.3s;
  }
  .sf-load-btn:hover { border-color: var(--gold); color: var(--gold); }

  /* FOOTER */
  .sf-footer {
    background: var(--dark); border-top: 1px solid var(--border);
    padding: 56px 60px;
    display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 56px;
  }
  .sf-footer-logo {
    font-family: 'SippyBold', sans-serif;
    font-size: 1.4rem; color: var(--gold);
    display: block; margin-bottom: 14px;
  }
  .sf-footer-logo span { color: var(--white); }
  .sf-footer p { color: var(--muted); font-size: 0.84rem; line-height: 1.85; }
  .sf-footer h4 { font-size: 0.7rem; letter-spacing: 0.15em; text-transform: uppercase; color: var(--gold); margin-bottom: 18px; }
  .sf-footer ul { list-style: none; }
  .sf-footer ul li { margin-bottom: 9px; }
  .sf-footer ul li a { color: var(--muted); text-decoration: none; font-size: 0.84rem; transition: color 0.2s; cursor: pointer; }
  .sf-footer ul li a:hover { color: var(--white); }
  .sf-footer-bottom {
    text-align: center; padding: 20px 60px;
    border-top: 1px solid var(--border);
    color: var(--muted); font-size: 0.75rem;
  }

  /* ANIMATIONS */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes scaleX {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }

  /* RESPONSIVE */
  @media (max-width: 1100px) {
    .sf-grid { grid-template-columns: repeat(2, 1fr); }
    .sf-with-sidebar { grid-template-columns: 1fr; }
    .sf-footer { grid-template-columns: 1fr 1fr; gap: 36px; }
  }
  @media (max-width: 768px) {
    .sf-featured { grid-template-columns: 1fr; margin: 20px; }
    .sf-feat-img { min-height: 260px; }
    .sf-grid { grid-template-columns: 1fr; margin: 0 20px; }
    .sf-newsletter { flex-direction: column; margin: 16px 20px; padding: 28px 24px; }
    .sf-nl-form { flex-direction: column; width: 100%; }
    .sf-nl-input { width: 100%; border-right: 1px solid var(--border); border-bottom: none; }
    .sf-filter, .sf-section-hd, .sf-with-sidebar, .sf-load-more { padding-left: 20px; padding-right: 20px; }
    .sf-with-sidebar { margin: 40px 20px 0; }
    .sf-footer { grid-template-columns: 1fr; padding: 40px 20px; gap: 28px; }
    .sf-footer-bottom { padding: 20px; }
  }

  .sf-empty { text-align: center; padding: 60px; color: var(--muted); }
`;

function useInView(ref, threshold = 0.1) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { threshold });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold]);
  return visible;
}

function FadeSection({ children, delay = 0, style = {} }) {
  const ref = useRef();
  const visible = useInView(ref);
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "none" : "translateY(24px)",
      transition: `opacity 0.65s ${delay}s ease, transform 0.65s ${delay}s ease`,
      ...style
    }}>
      {children}
    </div>
  );
}

export default function SippyFilmsNews() {
  const [featured, setFeatured] = useState(null);

  // all items for now, filter UI is commented out
const [newsItems, setNewsItems] = useState([]);


useEffect(() => {
  // Fetch grid news — /api/news is paginated:
  // { items, page, limit, total, totalPages }
  fetch(`${API_URL}/api/news?limit=${NEWS_PAGE_SIZE}`)
    .then(res => res.json())
    .then(data => setNewsItems(Array.isArray(data?.items) ? data.items : []))
    .catch(() => setNewsItems([]));

  // Fetch featured news
  fetch(`${API_URL}/api/featured-news`)
    .then(res => res.json())
    .then(data => setFeatured(data || {}))
    .catch(() => setFeatured({}));
}, []);

const filtered = newsItems;

  const hasNews = filtered && filtered.length > 0;
  const hasFeatured = Boolean(featured?.title);

  return (
    <>
      <style>{styles}</style>
      <div className="sf-page">

        {/* HERO */}
        <section className="sf-hero">
       
          <h1>NEWS</h1>
          <div className="sf-divider" />
        </section>

        {/* FEATURED STORY — managed from the admin dashboard */}
        {hasFeatured && (
          <FadeSection>
            <article className="sf-featured">
              <div className="sf-feat-img">
                {featured.image && (
                  <img src={featured.image} alt={featured.title} decoding="async" />
                )}
                {featured.category && (
                  <span className="sf-feat-tag">{featured.category}</span>
                )}
              </div>

              <div className="sf-feat-body">
                {featured.date && (
                  <div className="sf-feat-meta">{formatNewsDate(featured.date)}</div>
                )}
                <h2>{featured.title}</h2>
                {featured.excerpt && <p>{featured.excerpt}</p>}
                {featured.link && (
                  <a className="sf-read-btn" href={featured.link}>
                    Read more
                    <span className="visually-hidden">: {featured.title}</span>
                  </a>
                )}
              </div>
            </article>
          </FadeSection>
        )}

        {/* NEWS GRID */}
  {hasNews && (
  <FadeSection>
   <div className="sf-grid">
    {filtered.map(n => (
      <article className="sf-card" key={n._id}>
        <div className="sf-card-img">
          <img
            src={n.image}
            alt={n.title}
            loading="lazy"
            decoding="async"
          />
          <span className="sf-tag">
            {n.category}
          </span>
        </div>

        <div className="sf-card-body">
          <div className="sf-card-date">{formatNewsDate(n.date)}</div>
          <h3>{n.title}</h3>
          <p>{n.body}</p>

          <Link to="/contact" className="sf-readmore">
            Read More →
          </Link>
        </div>
      </article>
    ))}
  </div>
  </FadeSection>
  )}

  {hasNews && (
    <div className="sf-load-more">
      <button className="sf-load-btn">Load More Stories</button>
    </div>
  )}

  {!hasNews && (
    <div className="sf-empty">
      <p>No news available</p>
    </div>
  )}

  {/* FOOTER */}
        <footer className="sf-footer">
          <div>
            <span className="sf-footer-logo">SIPPY FILMS</span>
            <p>A legacy of storytelling spanning over six decades. From the golden age of Bollywood to contemporary streaming originals, Sippy Films continues to craft cinema that endures.</p>
          </div>
          <div>
            <h4>Navigate</h4>
            <ul>
              <li><Link to="/about-us">About Us</Link></li>
              <li><Link to="/latest">Latest News</Link></li>
              <li><Link to="/movies">Films</Link></li>
              <li><Link to="/contact">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4>Connect</h4>
            <ul>
              {["Instagram", "Twitter / X", "YouTube", "Facebook"].map(l => (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <li key={l}><a href="#">{l}</a></li>
              ))}
            </ul>
          </div>
        </footer>
        <div className="sf-footer-bottom">© 2026 Sippy Films. All rights reserved.</div>

      </div>
    </>
  );
}